import { DownloadRecordedRequest, DownloadRecordedRequestStatus, StaticVideo } from "../../../types/working-model";
import classNames from 'classnames';
import { convertMsToHMS, convertSecondsToHMS, getImageLocation } from "../../../utils/utils";
import { useMemo, useState } from "react";
import Icon, { COLORS, ICONS } from "../../general-ui/icon";
import EditableTitle from "../../general-ui/editable-title/editable-title";
import { useDispatch, useSelector } from "react-redux";
import { checkContent } from "../../../store/actions/admin/content-actions";
import { EventsState } from "../../../store/types";
import ModalComponent from "../../general-ui/modal/modal";
import { ListGridCheckbox } from "./document-item";
import { GetIbmDownloadLink, GetRecordedVideoDownload, GetWistiaDownloadLink } from "../../../connection/videos";
import { showAlert } from "../../general-ui/alert/alert-service";
import ReactPlayer from "react-player";
import './video-item.scss';
import LoadingSpinner from "../../general-ui/loading-spinner/loading-spinner";

interface VideoItemProps {
	video: StaticVideo;
}

enum IBMDownloadStatus {
	requested = 'requested',
	pending = 'pending',
	available = 'available',
	unavailable = 'unavailable'
}

function uploadUseCdn(link: string): string {
	return link.replace('https://brandlive-upload.s3-us-west-2.amazonaws.com', 'https://assets.brandnot.live');
}

export default function VideoItem({ video }: VideoItemProps): JSX.Element {
	const {
		ContentReducer: {
			checkedSet,
			view
		},
		AuthReducer: { token, user }
	} = useSelector((state: EventsState) => state);

	const [downloading, setDownloading] = useState(false);
	const [imgError, setImgError] = useState(false);
	const [isPlayerOpen, setIsPlayerOpen] = useState(false);
	const dispatch = useDispatch();

	const handleOpen = () => setIsPlayerOpen(true);
	const handleClose = () => setIsPlayerOpen(false);

	const handleCheck = () => {
		if (!isChecked && video.type === "wistia") {
			showAlert({
				message: "Cannot Delete Wistia Videos",
				description: "Please contact customer success for further assistance.",
				type: "error",
				duration: 3000
			});
			return;
		}
		dispatch(checkContent(video.video));
	};

	const isChecked = checkedSet.has(video.video);

	const handleImgError = () => setImgError(true);

	const [imgSrc, uploaded, duration, title, iframeLink] = useMemo(() => {
		let duration = "";

		if (video.type === 'recorded') {
			video.image = String(video.hls_link);
			video.duration = Number(video.duration);
			duration = convertMsToHMS(video.duration);
		} else {
			duration = convertSecondsToHMS(video.duration ?? 0);
		}

		const imgSrc = getImageLocation(video.image);
		const uploaded = video.created_date.toLocaleDateString();
		let link = "";

		if (video.type === "ustream") {
			link = `//www.ustream.tv/embed/recorded/${video.video}?html5ui=1&quality=med&wmode=transparent&ub=00a4e4&lc=00a4e4&oc=00a4e4&uc=00a4e4&rmalang=en_US`;
		}
		if (video.type === "wistia") {
			link = `//fast.wistia.net/embed/iframe/${video.video}`;
		}

		return [imgSrc, uploaded, duration, video.name, link];
	}, [video]);

	const listView = view === "list";
	const gridView = view === "grid";

	const handleDownload = async () => {
		if (video.type === "ustream") {
			const downloadable = await GetIbmDownloadLink(token!, video.video as string);

			if ('status' in downloadable) {
				switch (downloadable.status) {
					case IBMDownloadStatus.available: {
						const link = document.createElement('a');
						link.href = downloadable.response.downloadable.download_url;
						link.download = 'true';
						link.click();
						break;
					}
					case IBMDownloadStatus.requested:
					case IBMDownloadStatus.pending: {
						showAlert({
							message: "Download Processing",
							description: "A download link is being generated for this file. A link will be sent to your email when it is ready.",
							duration: 5000
						});
						break;
					}
					case IBMDownloadStatus.unavailable: {
						showAlert({
							message: "Unavailable",
							description: "Unfortunately we cannot download this video. Please contact support for assistance.",
							duration: 7000,
							type: "error"
						});
						break;
					}
					default: {
						console.log(downloadable);
					}
				}
			}
		}
		if (video.type === "wistia") {
			const { assets } = await GetWistiaDownloadLink(token!, video.video as string);

			//this is going to get deprecated soon, not worth the effort of making a type for it.
			const largestAsset = assets.filter((asset: any) => asset.contentType === 'video/mp4').sort((a: any, b: any) => {
				if (a.fileSize > b.fileSize) {
					return -1;
				} else {
					return 1;
				}
			})[0];

			if (largestAsset) {
				const link = document.createElement('a');
				link.href = largestAsset.url.replace("http://embed.", 'https://embed-ssl.').replace('.bin', '.mp4');
				link.download = "true";
				link.target = "_blank";
				link.click();
			} else {
				showAlert({
					message: "Unavailable",
					description: "Unfortunately we cannot download this video. Please contact support for assistance.",
					duration: 7000,
					type: "error"
				});
			}
		}
		if (video.type === "uploaded") {
			if (video.source) {
				const link = document.createElement('a');
				link.href = video.source;
				link.download = "true";
				link.target = "_blank";
				link.click();
			}
		}
		if (video.type === "recorded") {
			try {
				setDownloading(true);
				const recording = await GetRecordedVideoDownload(token!, video.uuid!) as DownloadRecordedRequest;

				if (recording) {
					switch (recording.status) {
						case DownloadRecordedRequestStatus.completed: {
							const link = document.createElement('a');
							link.href = recording.result;
							link.download = "recording.mp4";
							link.target = "_blank";
							link.click();
							break;
						}
						case DownloadRecordedRequestStatus.pending:
						case DownloadRecordedRequestStatus.processing: {
							showAlert({
								message: "Download Processing",
								description: `We are generating a downloadable version. You will be sent an email at ${user!.email} when completed.`,
								duration: 6000
							});
							break;
						}
					}
				}

				setDownloading(false);
			} catch (e) {
				console.error(e);
				showAlert({
					message: "Error Downloading Video",
					description: "We ran into an error downloading that video. Please try again.",
					duration: 5000
				});
			}

		}
	};

	return (
		<li className={classNames("content-item", `${view}-item`)} >
			{gridView && (
				<div className={classNames("thumbnail", { error: imgError || !imgSrc })}>
					{(!imgError && imgSrc)
						? <img
							className="thumbnail"
							src={imgSrc}
							onError={handleImgError}
							alt="" />
						: <Icon name={ICONS.CAMERA_ON} size={64} color="" />
					}
					<div className="play-container" onClick={handleOpen}>
						<Icon name={ICONS.PLAY} size={30} color="white" />
					</div>
					<div className="download-button">
						<button onClick={handleDownload}>{downloading ? <LoadingSpinner height={12} width={12} stroke={6} /> : <Icon name={ICONS.ARROW_DOWN} size={12} color={COLORS.WHITE} />}</button>
					</div>
				</div>
			)}
			<ListGridCheckbox id={video.video} isChecked={isChecked} onChange={handleCheck} gridView={gridView} />
			<EditableTitle
				defaultText={title}
				onSubmit={() => null}
				className="item-title"
			/>
			<p className={`duration-${view}`}>{duration}</p>

			{listView && (
				<>
					<p>{uploaded}</p>
					<hr className="hr" />
				</>
			)}
			<div className="video-modal">

			</div>
			<ModalComponent
				title={title}
				open={isPlayerOpen}
				onRequestClose={handleClose}
				cancellable
				closeable={false}
				footer={<button onClick={handleClose}>Close</button>}
			>
				<div
					style={{ position: 'relative', paddingBottom: '56%' }}
				>
					{iframeLink ? (
						<iframe
							title={title}
							width="100%"
							style={{ borderRadius: 12, position: 'absolute', height: '100%', width: '100%', border: 'none' }}
							id="PlayerIframe"
							src={iframeLink}
							allowFullScreen
							referrerPolicy="no-referrer-when-downgrade">
						</iframe>
					) : (
						<>
							{(video.source || video.hls_link) && (
								<ReactPlayer
									url={(video.hls_link ? uploadUseCdn(video.hls_link) : uploadUseCdn(video.source ?? '')) as string}
									playing={true}
									width={'100%'}
									height={'100%'}
									config={{
										file: {
											forceHLS: !!video.hls_link
										}
									}}
									style={{
										position: 'absolute'
									}}
									controls={true}
								/>
							)}
						</>

					)}
				</div>

			</ModalComponent>
		</li>
	);
}
